// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the phone video viewer, YouTube-style (JP picked concept A, 2026-10-28): back 10 / play /
// forward 10 over the picture, a glass panel in the thumb zone, double tap to jump, hold for 2×,
// a frame preview while scrubbing (slide up to seek finely), Replay and the next clip at the end.

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {
	CLOSE_DESCRIPTOR,
	MORE_OPTIONS_DESCRIPTOR,
	PAUSE_DESCRIPTOR,
	PLAY_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {isKeyboardActivationKey} from '@app/features/input/utils/KeyboardUtils';
import {PanZoomSurface} from '@app/features/messaging/components/modals/media_modal/pan_zoom/PanZoomSurface';
import type {ZoomState} from '@app/features/messaging/components/modals/media_modal/shared';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {GlassMenu} from '@app/features/ui/glass_menu/GlassMenu';
import {
	CaretRightIcon,
	DeviceRotateIcon,
	DotsThreeIcon,
	PauseSolidIcon,
	PictureInPictureIcon,
	PlaySolidIcon,
	RepeatIcon,
	ReplayIcon,
	RewindBackTenIcon,
	RewindForwardTenIcon,
	SpeakerHighIcon,
	SpeakerXIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import {VideoSeekPreview} from '@app/features/voice/components/media_player/components/VideoSeekPreview';
import {
	clampMediaTime,
	getBufferedPercentage,
	getEffectiveMediaDuration,
	type SeekDirection,
} from '@app/features/voice/components/media_player/utils/MediaSeekUtils';
import styles from '@app/features/voice/components/modals/MobileVideoViewer.module.css';
import {useInAppMediaSoundCapture} from '@app/features/voice/hooks/useInAppMediaSoundCapture';
import VideoPlayback, {formatSpeed, VIDEO_SPEEDS} from '@app/features/voice/state/VideoPlayback';
import VideoVolume from '@app/features/voice/state/VideoVolume';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

const TOGGLE_CONTROLS_DESCRIPTOR = msg({
	message: 'Toggle controls',
	comment: 'Aria label on the tap-to-toggle area of the mobile video viewer. Shows or hides the playback controls.',
});
const UNMUTE_DESCRIPTOR = msg({
	message: 'Unmute',
	comment: 'Mute toggle button label in the mobile video viewer (currently muted).',
});
const MUTE_DESCRIPTOR = msg({
	message: 'Mute',
	comment: 'Mute toggle button label in the mobile video viewer (currently unmuted).',
});
const VIDEO_PROGRESS_DESCRIPTOR = msg({
	message: 'Video progress',
	comment: 'Aria label on the video scrubber / progress slider in the mobile video viewer.',
});
export const BACK_TEN_DESCRIPTOR = msg({
	message: 'Back 10 seconds',
	comment: 'Button in the video player that jumps back 10 seconds.',
});
export const FORWARD_TEN_DESCRIPTOR = msg({
	message: 'Forward 10 seconds',
	comment: 'Button in the video player that jumps forward 10 seconds.',
});
const REPLAY_DESCRIPTOR = msg({
	message: 'Replay',
	comment: 'Button shown when a video has ended; plays it again from the start.',
});
export const PLAYBACK_SPEED_DESCRIPTOR = msg({
	message: 'Playback speed',
	comment: 'Video player: the button and menu that choose how fast the video plays (0.5×, Normal, 2×).',
});
export const NORMAL_SPEED_DESCRIPTOR = msg({
	message: 'Normal',
	comment: 'Video player speed choice for the normal speed (1×).',
});
export const LOOP_DESCRIPTOR = msg({
	message: 'Loop',
	comment: 'Video player switch: play the video again from the start when it ends.',
});
export const FLOAT_DESCRIPTOR = msg({
	message: 'Picture in picture',
	comment: 'Video player button: keep the video playing in a small floating window over other apps.',
});
const FULL_SCREEN_DESCRIPTOR = msg({
	message: 'Full screen',
	comment: 'Video player button on phones: play the video full screen, turned sideways.',
});
const SEEK_SECONDS_DESCRIPTOR = msg({
	message: '{seconds} seconds',
	comment:
		'Shown over the video while double tapping to jump; seconds is how far it jumped in total (10, 20, 30). Arrows show the direction.',
});
const FINE_SEEK_HINT_DESCRIPTOR = msg({
	message: 'Slide up for fine seeking',
	comment: 'Hint while dragging the video progress bar: moving the finger up makes the drag move in smaller steps.',
});
const FINE_SEEKING_DESCRIPTOR = msg({
	message: 'Fine seeking',
	comment: 'Shown while dragging the video progress bar with the finger moved up: the drag now moves in small steps.',
});
const NEXT_IN_CHAT_DESCRIPTOR = msg({
	message: 'Next in this chat',
	comment: 'Label on the card shown when a video ends: opens the next video or picture from the same chat.',
});
const PLAYING_FASTER_DESCRIPTOR = msg({
	message: 'Playing at 2×',
	comment: 'Aria live text while the user holds the video to play it at double speed.',
});

const SEEK_STEP_SECONDS = 10;
const SINGLE_TAP_DELAY_MS = 260;
const SEEK_STREAK_MS = 700;
const HOLD_FOR_FAST_MS = 450;
const HOLD_MOVE_TOLERANCE = 12;
const FAST_RATE = 2;
const HUD_HIDE_MS = 3000;
/** How far up (px) the finger must slide while scrubbing before the drag moves in fine steps. */
const FINE_SEEK_LIFT = 48;
const FINE_SEEK_FACTOR = 0.2;

export interface MobileVideoNext {
	title: string;
	subtitle?: string;
	/** The next clip itself (a still from it is shown); a picture shows as itself. */
	src: string;
	isVideo: boolean;
	onOpen: () => void;
}

interface MobileVideoViewerProps {
	src: string;
	initialTime?: number;
	/** What the clip is (its file name) and where it is from, at the top. */
	title?: string;
	subtitle?: string;
	/** Loop no matter what the Loop switch says (short clips that are meant to loop). */
	loop?: boolean;
	/** What comes after this clip in the chat, offered when it ends. */
	next?: MobileVideoNext;
	onClose: () => void;
	/** The ⋯ was pressed; the menu drops from `anchor`. */
	onMenuOpen?: (anchor?: HTMLElement) => void;
	/** KONVO: a clip in a set (a WhatsApp album) steps to its neighbours with a sideways swipe. */
	onSwipePrevious?: () => void;
	onSwipeNext?: () => void;
}

interface SeekRipple {
	id: number;
	direction: SeekDirection;
	seconds: number;
	box: PictureBox;
}

interface PictureBox {
	left: number;
	top: number;
	width: number;
	height: number;
}

interface TapPoint {
	x: number;
	width: number;
	time: number;
}

type WebKitVideo = HTMLVideoElement & {webkitEnterFullscreen?: () => void};
type LockableOrientation = ScreenOrientation & {lock?: (orientation: string) => Promise<void>; unlock?: () => void};

function formatTime(time: number): string {
	if (!Number.isFinite(time) || time < 0) return '0:00';
	const hours = Math.floor(time / 3600);
	const minutes = Math.floor((time % 3600) / 60);
	const seconds = Math.floor(time % 60)
		.toString()
		.padStart(2, '0');
	return hours > 0 ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
}

/** Where the picture actually sits inside the (letterboxed) video element, in container pixels. */
function pictureBox(video: HTMLVideoElement, container: HTMLElement): PictureBox {
	const rect = video.getBoundingClientRect();
	const origin = container.getBoundingClientRect();
	const {videoWidth, videoHeight} = video;
	if (!videoWidth || !videoHeight) {
		return {left: rect.left - origin.left, top: rect.top - origin.top, width: rect.width, height: rect.height};
	}
	const scale = Math.min(rect.width / videoWidth, rect.height / videoHeight);
	const width = videoWidth * scale;
	const height = videoHeight * scale;
	return {
		left: rect.left - origin.left + (rect.width - width) / 2,
		top: rect.top - origin.top + (rect.height - height) / 2,
		width,
		height,
	};
}

export const MobileVideoViewer = observer(function MobileVideoViewer({
	src,
	initialTime,
	title,
	subtitle,
	loop: forceLoop,
	next,
	onClose,
	onMenuOpen,
	onSwipePrevious,
	onSwipeNext,
}: MobileVideoViewerProps) {
	const {i18n} = useLingui();
	const containerRef = useRef<HTMLDivElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const speedButtonRef = useRef<HTMLButtonElement>(null);
	useInAppMediaSoundCapture(videoRef);
	const loop = forceLoop ?? VideoPlayback.loop;
	const rate = VideoPlayback.rate;
	const [isPlaying, setIsPlaying] = useState(false);
	const [isEnded, setIsEnded] = useState(false);
	const [zoomState, setZoomState] = useState<ZoomState>('fit');
	const [zoomResetKey, setZoomResetKey] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [aspect, setAspect] = useState(16 / 9);
	const [bufferedProgress, setBufferedProgress] = useState(0);
	const [hudVisible, setHudVisible] = useState(true);
	const [isScrubbing, setIsScrubbing] = useState(false);
	const [isFineSeeking, setIsFineSeeking] = useState(false);
	const [scrubProgress, setScrubProgress] = useState<number | null>(null);
	const [ripple, setRipple] = useState<SeekRipple | null>(null);
	const [isFast, setIsFast] = useState(false);
	const [speedMenuOpen, setSpeedMenuOpen] = useState(false);
	const [canFullscreen, setCanFullscreen] = useState(false);
	const hudTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const rippleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const singleTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const holdStartRef = useRef<{x: number; y: number; id: number} | null>(null);
	const holdUsedRef = useRef(false);
	const seekStreakRef = useRef<{direction: SeekDirection; seconds: number; id: number; time: number} | null>(null);
	const progressRectRef = useRef<DOMRect | null>(null);
	const activeProgressPointerIdRef = useRef<number | null>(null);
	const scrubRef = useRef<{startY: number; anchorX: number; anchorProgress: number; progress: number} | null>(null);
	const pendingTapPointRef = useRef<TapPoint | null>(null);
	const previousTapPointRef = useRef<TapPoint | null>(null);
	const hasInitializedRef = useRef(false);
	const safelyPlayVideo = useCallback((video: HTMLVideoElement) => {
		const playPromise = video.play();
		void playPromise?.catch(() => {});
	}, []);
	const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;
	const displayProgress = scrubProgress ?? progress;
	const displayCurrentTime = duration > 0 ? displayProgress * duration : currentTime;
	const clearHudTimer = useCallback(() => {
		if (!hudTimerRef.current) return;
		clearTimeout(hudTimerRef.current);
		hudTimerRef.current = null;
	}, []);
	const scheduleHudHide = useCallback(() => {
		clearHudTimer();
		hudTimerRef.current = setTimeout(() => {
			const video = videoRef.current;
			if (video && !video.paused && !video.ended) setHudVisible(false);
			hudTimerRef.current = null;
		}, HUD_HIDE_MS);
	}, [clearHudTimer]);
	const showHud = useCallback(() => {
		setHudVisible(true);
		scheduleHudHide();
	}, [scheduleHudHide]);
	const commitSeekToTime = useCallback(
		(time: number) => {
			const video = videoRef.current;
			if (!video) return;
			const effectiveDuration = getEffectiveMediaDuration(video, duration);
			if (!effectiveDuration) return;
			const nextTime = clampMediaTime(time, effectiveDuration);
			video.currentTime = nextTime;
			setCurrentTime(nextTime);
			setScrubProgress(null);
			if (nextTime < effectiveDuration) setIsEnded(false);
		},
		[duration],
	);
	const seekRelative = useCallback(
		(deltaSeconds: number) => {
			const video = videoRef.current;
			if (!video) return;
			commitSeekToTime(video.currentTime + deltaSeconds);
		},
		[commitSeekToTime],
	);
	// Each double tap (and each further tap on the same side right after) jumps 10 s more.
	const jump = useCallback(
		(direction: SeekDirection) => {
			const video = videoRef.current;
			const container = containerRef.current;
			if (!video || !container) return;
			const now = performance.now();
			const streak = seekStreakRef.current;
			const continuing = streak !== null && streak.direction === direction && now - streak.time < SEEK_STREAK_MS;
			const seconds = continuing ? streak.seconds + SEEK_STEP_SECONDS : SEEK_STEP_SECONDS;
			const id = continuing ? streak.id : now;
			seekStreakRef.current = {direction, seconds, id, time: now};
			seekRelative(direction === 'forward' ? SEEK_STEP_SECONDS : -SEEK_STEP_SECONDS);
			setRipple({id, direction, seconds, box: pictureBox(video, container)});
			if (rippleTimerRef.current) clearTimeout(rippleTimerRef.current);
			rippleTimerRef.current = setTimeout(() => {
				setRipple(null);
				rippleTimerRef.current = null;
			}, SEEK_STREAK_MS);
		},
		[seekRelative],
	);
	const handleTapSurface = useCallback(() => {
		const tap = pendingTapPointRef.current;
		pendingTapPointRef.current = null;
		// The finger that held the clip at 2× lifts: that is not a tap.
		if (holdUsedRef.current) {
			holdUsedRef.current = false;
			return;
		}
		const side: SeekDirection | null = tap ? (tap.x / tap.width < 0.5 ? 'backward' : 'forward') : null;
		const streak = seekStreakRef.current;
		if (tap && side && streak && streak.direction === side && tap.time - streak.time < SEEK_STREAK_MS) {
			jump(side);
			return;
		}
		const previous = previousTapPointRef.current;
		const isDoubleTap =
			tap !== null &&
			previous !== null &&
			tap.time - previous.time < SINGLE_TAP_DELAY_MS + 100 &&
			side !== null &&
			(previous.x / previous.width < 0.5 ? 'backward' : 'forward') === side &&
			Math.abs(tap.x / tap.width - 0.5) > 0.08;
		if (isDoubleTap && side) {
			previousTapPointRef.current = null;
			if (singleTapTimerRef.current) {
				clearTimeout(singleTapTimerRef.current);
				singleTapTimerRef.current = null;
			}
			jump(side);
			return;
		}
		previousTapPointRef.current = tap;
		// One tap shows or hides the controls, once it is clear a second tap is not coming.
		if (singleTapTimerRef.current) clearTimeout(singleTapTimerRef.current);
		singleTapTimerRef.current = setTimeout(() => {
			singleTapTimerRef.current = null;
			setHudVisible((visible) => {
				if (!visible) scheduleHudHide();
				return !visible;
			});
		}, SINGLE_TAP_DELAY_MS);
	}, [jump, scheduleHudHide]);
	const togglePlay = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		if (video.ended || isEnded) {
			video.currentTime = 0;
			setIsEnded(false);
			safelyPlayVideo(video);
			return;
		}
		if (video.paused) {
			safelyPlayVideo(video);
			setZoomResetKey((key) => key + 1);
		} else {
			video.pause();
		}
	}, [isEnded, safelyPlayVideo]);
	const stop = useCallback((e: React.SyntheticEvent) => e.stopPropagation(), []);
	const handlePlayPause = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			togglePlay();
		},
		[togglePlay],
	);
	const handleJumpButton = useCallback(
		(e: React.MouseEvent, direction: SeekDirection) => {
			e.stopPropagation();
			seekRelative(direction === 'forward' ? SEEK_STEP_SECONDS : -SEEK_STEP_SECONDS);
			showHud();
		},
		[seekRelative, showHud],
	);
	const handleToggleMute = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		VideoVolume.toggleMute();
	}, []);
	const handleToggleLoop = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			VideoPlayback.setLoop(!VideoPlayback.loop);
			showHud();
		},
		[showHud],
	);
	const handleFloat = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		VideoPlayback.float();
	}, []);
	const handleOpenSpeed = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			clearHudTimer();
			setSpeedMenuOpen(true);
		},
		[clearHudTimer],
	);
	const closeSpeedMenu = useCallback(() => {
		setSpeedMenuOpen(false);
		scheduleHudHide();
	}, [scheduleHudHide]);
	// Android goes full screen and turns sideways; iPhone hands the clip to its own full-screen player.
	const handleFullscreen = useCallback(async (e: React.MouseEvent) => {
		e.stopPropagation();
		const container = containerRef.current;
		const video = videoRef.current as WebKitVideo | null;
		if (!container || !video) return;
		const orientation = (typeof screen !== 'undefined' ? screen.orientation : undefined) as
			| LockableOrientation
			| undefined;
		if (document.fullscreenElement) {
			orientation?.unlock?.();
			await document.exitFullscreen().catch(() => {});
			return;
		}
		if (typeof container.requestFullscreen === 'function') {
			try {
				await container.requestFullscreen({navigationUI: 'hide'});
				await orientation?.lock?.('landscape').catch(() => {});
				return;
			} catch {}
		}
		video.webkitEnterFullscreen?.();
	}, []);
	const handleNext = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			next?.onOpen();
		},
		[next],
	);
	const progressFromClientX = useCallback((clientX: number): number => {
		const rect = progressRectRef.current;
		if (!rect || rect.width <= 0) return 0;
		return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
	}, []);
	const handleProgressPointerDown = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			if (e.pointerType === 'mouse' && e.button !== 0) return;
			if (duration <= 0) return;
			e.preventDefault();
			e.stopPropagation();
			clearHudTimer();
			progressRectRef.current = e.currentTarget.getBoundingClientRect();
			activeProgressPointerIdRef.current = e.pointerId;
			e.currentTarget.setPointerCapture?.(e.pointerId);
			const start = progressFromClientX(e.clientX);
			scrubRef.current = {startY: e.clientY, anchorX: e.clientX, anchorProgress: start, progress: start};
			setHudVisible(true);
			setIsScrubbing(true);
			setIsFineSeeking(false);
			setScrubProgress(start);
		},
		[clearHudTimer, duration, progressFromClientX],
	);
	const handleProgressPointerMove = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			if (activeProgressPointerIdRef.current !== e.pointerId) return;
			const scrub = scrubRef.current;
			const rect = progressRectRef.current;
			if (!scrub || !rect) return;
			e.preventDefault();
			e.stopPropagation();
			// Slid up: the drag moves a fifth as far from where fine seeking began. Back down, it follows the finger.
			const fine = scrub.startY - e.clientY > FINE_SEEK_LIFT;
			let next: number;
			if (fine) {
				next = scrub.anchorProgress + ((e.clientX - scrub.anchorX) / rect.width) * FINE_SEEK_FACTOR;
				next = Math.max(0, Math.min(1, next));
			} else {
				next = progressFromClientX(e.clientX);
				scrub.anchorX = e.clientX;
				scrub.anchorProgress = next;
			}
			scrub.progress = next;
			setIsFineSeeking(fine);
			setScrubProgress(next);
		},
		[progressFromClientX],
	);
	const finishScrub = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			if (activeProgressPointerIdRef.current !== e.pointerId) return;
			e.preventDefault();
			e.stopPropagation();
			const final = scrubRef.current?.progress ?? progress;
			if (duration > 0) commitSeekToTime(final * duration);
			activeProgressPointerIdRef.current = null;
			progressRectRef.current = null;
			scrubRef.current = null;
			e.currentTarget.releasePointerCapture?.(e.pointerId);
			setIsScrubbing(false);
			setIsFineSeeking(false);
			setScrubProgress(null);
			scheduleHudHide();
		},
		[commitSeekToTime, duration, progress, scheduleHudHide],
	);
	const handleProgressKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (duration <= 0) return;
			let nextTime = displayCurrentTime;
			if (e.key === 'ArrowLeft') nextTime -= 5;
			else if (e.key === 'ArrowRight') nextTime += 5;
			else if (e.key === 'Home') nextTime = 0;
			else if (e.key === 'End') nextTime = duration;
			else return;
			e.preventDefault();
			e.stopPropagation();
			commitSeekToTime(nextTime);
			showHud();
		},
		[commitSeekToTime, displayCurrentTime, duration, showHud],
	);
	const endHold = useCallback(() => {
		if (holdTimerRef.current) {
			clearTimeout(holdTimerRef.current);
			holdTimerRef.current = null;
		}
		holdStartRef.current = null;
		const video = videoRef.current;
		if (video && video.playbackRate === FAST_RATE && VideoPlayback.rate !== FAST_RATE)
			video.playbackRate = VideoPlayback.rate;
		setIsFast(false);
	}, []);
	// Hold the playing clip: it plays at 2× until the finger lifts. The zoom surface captures the
	// pointer, so the press is followed on the whole viewer (capture phase), not on the video.
	const isFastRef = useRef(false);
	isFastRef.current = isFast;
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		const onControls = (event: PointerEvent) =>
			event.target instanceof Element && event.target.closest('button, [role="slider"]') !== null;
		const onDown = (event: PointerEvent) => {
			holdUsedRef.current = false;
			endHold();
			const video = videoRef.current;
			if (event.pointerType === 'mouse' || !event.isPrimary || onControls(event)) return;
			if (!video || video.paused || video.ended) return;
			holdStartRef.current = {x: event.clientX, y: event.clientY, id: event.pointerId};
			holdTimerRef.current = setTimeout(() => {
				holdTimerRef.current = null;
				if (!holdStartRef.current || video.paused) return;
				holdUsedRef.current = true;
				video.playbackRate = FAST_RATE;
				setIsFast(true);
				setHudVisible(false);
			}, HOLD_FOR_FAST_MS);
		};
		const onMove = (event: PointerEvent) => {
			const start = holdStartRef.current;
			if (!start || start.id !== event.pointerId || isFastRef.current) return;
			if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > HOLD_MOVE_TOLERANCE) endHold();
		};
		const onUp = (event: PointerEvent) => {
			endHold();
			if (onControls(event)) return;
			const rect = container.getBoundingClientRect();
			pendingTapPointRef.current = {x: event.clientX - rect.left, width: rect.width, time: performance.now()};
		};
		container.addEventListener('pointerdown', onDown, true);
		container.addEventListener('pointermove', onMove, true);
		container.addEventListener('pointerup', onUp, true);
		container.addEventListener('pointercancel', endHold, true);
		return () => {
			container.removeEventListener('pointerdown', onDown, true);
			container.removeEventListener('pointermove', onMove, true);
			container.removeEventListener('pointerup', onUp, true);
			container.removeEventListener('pointercancel', endHold, true);
		};
	}, [endHold]);
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;
		VideoPlayback.setActiveVideo(video);
		setCanFullscreen(
			typeof containerRef.current?.requestFullscreen === 'function' ||
				typeof (video as WebKitVideo).webkitEnterFullscreen === 'function',
		);
		return () => {
			if (VideoPlayback.activeVideo === video) VideoPlayback.setActiveVideo(null);
		};
	}, []);
	useEffect(() => {
		const video = videoRef.current;
		if (!video || isFast) return;
		video.defaultPlaybackRate = rate;
		video.playbackRate = rate;
	}, [rate, isFast]);
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;
		const handlePlay = () => {
			setIsPlaying(true);
			setIsEnded(false);
			scheduleHudHide();
		};
		const handlePause = () => {
			setIsPlaying(false);
			setHudVisible(true);
			clearHudTimer();
		};
		const handleEnded = () => {
			setIsPlaying(false);
			setIsEnded(true);
			setHudVisible(true);
			clearHudTimer();
		};
		const updateBuffered = () => setBufferedProgress(getBufferedPercentage(video) / 100);
		const handleTimeUpdate = () => {
			setCurrentTime(video.currentTime);
			updateBuffered();
		};
		const handleLoadedMetadata = () => {
			const nextDuration = getEffectiveMediaDuration(video);
			setDuration(nextDuration);
			if (video.videoWidth > 0 && video.videoHeight > 0) setAspect(video.videoWidth / video.videoHeight);
			video.playbackRate = VideoPlayback.rate;
			updateBuffered();
			if (initialTime !== undefined && !hasInitializedRef.current) {
				hasInitializedRef.current = true;
				const startTime = clampMediaTime(initialTime, nextDuration);
				video.currentTime = startTime;
				setCurrentTime(startTime);
				safelyPlayVideo(video);
			}
		};
		const handleDurationChange = () => {
			setDuration(getEffectiveMediaDuration(video));
			updateBuffered();
		};
		video.addEventListener('play', handlePlay);
		video.addEventListener('pause', handlePause);
		video.addEventListener('ended', handleEnded);
		video.addEventListener('timeupdate', handleTimeUpdate);
		video.addEventListener('loadedmetadata', handleLoadedMetadata);
		video.addEventListener('durationchange', handleDurationChange);
		video.addEventListener('progress', updateBuffered);
		video.addEventListener('canplay', updateBuffered);
		return () => {
			video.removeEventListener('play', handlePlay);
			video.removeEventListener('pause', handlePause);
			video.removeEventListener('ended', handleEnded);
			video.removeEventListener('timeupdate', handleTimeUpdate);
			video.removeEventListener('loadedmetadata', handleLoadedMetadata);
			video.removeEventListener('durationchange', handleDurationChange);
			video.removeEventListener('progress', updateBuffered);
			video.removeEventListener('canplay', updateBuffered);
		};
	}, [clearHudTimer, initialTime, scheduleHudHide, safelyPlayVideo]);
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;
		video.muted = VideoVolume.isMuted;
	}, []);
	useEffect(() => {
		return () => {
			clearHudTimer();
			for (const timer of [rippleTimerRef, singleTapTimerRef, holdTimerRef]) {
				if (timer.current) clearTimeout(timer.current);
				timer.current = null;
			}
		};
	}, [clearHudTimer]);
	const handleZoomStateChange = useCallback((state: ZoomState) => {
		setZoomState((previousState) => (previousState === state ? previousState : state));
	}, []);
	const handleClose = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onClose();
		},
		[onClose],
	);
	const handleMenuOpen = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			clearHudTimer();
			onMenuOpen?.(e.currentTarget as HTMLElement);
		},
		[clearHudTimer, onMenuOpen],
	);
	const previewWidth = aspect >= 1 ? 176 : 96;
	const previewHeight = Math.round(previewWidth / aspect);
	const speedGroups = [
		{
			items: VIDEO_SPEEDS.map((speed) => ({
				label: speed === 1 ? i18n._(NORMAL_SPEED_DESCRIPTOR) : formatSpeed(speed),
				selected: rate === speed,
				onSelect: () => {
					VideoPlayback.setRate(speed);
					closeSpeedMenu();
				},
			})),
		},
	];
	const showControls = hudVisible && zoomState === 'fit' && !isFast;
	return (
		<div ref={containerRef} className={styles.container} data-flx="voice.mobile-video-viewer.container">
			<PanZoomSurface
				className={styles.zoomSurface}
				contentClassName={styles.zoomContent}
				zoomState={zoomState}
				maxScale={5}
				zoomedScale={2.25}
				panDisabled={isPlaying}
				onSwipePrevious={onSwipePrevious}
				onSwipeNext={onSwipeNext}
				onSwipeDownDismiss={onClose}
				doubleClickEnabled={false}
				tapToToggleZoom={false}
				resetKey={zoomResetKey}
				onZoomStateChange={handleZoomStateChange}
				onTap={handleTapSurface}
				onKeyDown={(e) => {
					if (isKeyboardActivationKey(e.key)) {
						e.preventDefault();
						togglePlay();
					}
				}}
				role="button"
				tabIndex={0}
				aria-label={i18n._(TOGGLE_CONTROLS_DESCRIPTOR)}
				data-flx="voice.mobile-video-viewer.pan-zoom-surface"
			>
				<video
					ref={videoRef}
					className={styles.video}
					src={src}
					autoPlay={initialTime === undefined}
					playsInline
					loop={loop}
					muted={VideoVolume.isMuted}
					onContextMenu={(e) => e.preventDefault()}
					data-flx="voice.mobile-video-viewer.video"
				/>
			</PanZoomSurface>
			<AnimatePresence data-flx="voice.mobile-video-viewer.seek-feedback-presence">
				{ripple && (
					<motion.div
						key={ripple.id}
						className={clsx(styles.ripple, ripple.direction === 'backward' ? styles.rippleBack : styles.rippleForward)}
						style={{
							top: ripple.box.top,
							height: ripple.box.height,
							...(ripple.direction === 'backward'
								? {left: ripple.box.left, width: ripple.box.width * 0.44}
								: {left: ripple.box.left + ripple.box.width * 0.56, width: ripple.box.width * 0.44}),
						}}
						initial={{opacity: 0}}
						animate={{opacity: 1}}
						exit={{opacity: 0}}
						transition={{duration: Accessibility.useReducedMotion ? 0 : 0.18}}
						aria-hidden="true"
						data-flx="voice.mobile-video-viewer.seek-feedback"
					>
						<span className={styles.rippleArrows} key={ripple.seconds}>
							<PlaySolidIcon size={remFromPx(18)} data-flx="voice.mobile-video-viewer.ripple-arrow" />
							<PlaySolidIcon size={remFromPx(18)} data-flx="voice.mobile-video-viewer.ripple-arrow--2" />
							<PlaySolidIcon size={remFromPx(18)} data-flx="voice.mobile-video-viewer.ripple-arrow--3" />
						</span>
						<span className={styles.rippleSeconds}>{i18n._(SEEK_SECONDS_DESCRIPTOR, {seconds: ripple.seconds})}</span>
					</motion.div>
				)}
			</AnimatePresence>
			<AnimatePresence data-flx="voice.mobile-video-viewer.fast-presence">
				{isFast && (
					<motion.div
						className={clsx(styles.glass, styles.fastPill)}
						initial={{opacity: 0, y: Accessibility.useReducedMotion ? 0 : -6}}
						animate={{opacity: 1, y: 0}}
						exit={{opacity: 0}}
						transition={{duration: Accessibility.useReducedMotion ? 0 : 0.15}}
						role="status"
						aria-label={i18n._(PLAYING_FASTER_DESCRIPTOR)}
						data-flx="voice.mobile-video-viewer.fast-pill"
					>
						2×
						<span className={styles.fastArrows} aria-hidden="true">
							<PlaySolidIcon size={remFromPx(11)} data-flx="voice.mobile-video-viewer.fast-arrow" />
							<PlaySolidIcon size={remFromPx(11)} data-flx="voice.mobile-video-viewer.fast-arrow--2" />
						</span>
					</motion.div>
				)}
			</AnimatePresence>
			<AnimatePresence data-flx="voice.mobile-video-viewer.animate-presence">
				{showControls && (
					<motion.div
						className={styles.hudOverlay}
						initial={{opacity: 0}}
						animate={{opacity: 1}}
						exit={{opacity: 0}}
						transition={{duration: Accessibility.useReducedMotion ? 0 : 0.15}}
						data-flx="voice.mobile-video-viewer.hud-overlay"
					>
						<div className={clsx(styles.dim, isEnded && styles.dimEnded)} aria-hidden="true" />
						<div className={styles.topBar} data-flx="voice.mobile-video-viewer.top-bar">
							<button
								type="button"
								className={clsx(styles.glass, styles.topBarButton)}
								onClick={handleClose}
								aria-label={i18n._(CLOSE_DESCRIPTOR)}
								data-flx="voice.mobile-video-viewer.top-bar-button.close"
							>
								<XIcon size={remFromPx(20)} weight="bold" data-flx="voice.mobile-video-viewer.x-icon" />
							</button>
							<div className={styles.title} data-flx="voice.mobile-video-viewer.title">
								{title && <span className={styles.titleName}>{title}</span>}
								{subtitle && <span className={styles.titleMeta}>{subtitle}</span>}
							</div>
							{onMenuOpen && (
								<button
									type="button"
									className={clsx(styles.glass, styles.topBarButton)}
									onClick={handleMenuOpen}
									aria-label={i18n._(MORE_OPTIONS_DESCRIPTOR)}
									data-flx="voice.mobile-video-viewer.top-bar-button.menu-open"
								>
									<DotsThreeIcon
										size={remFromPx(20)}
										weight="bold"
										data-flx="voice.mobile-video-viewer.dots-three-icon"
									/>
								</button>
							)}
						</div>
						{isScrubbing ? (
							<div className={styles.scrubHint} aria-hidden="true" data-flx="voice.mobile-video-viewer.scrub-hint">
								{i18n._(isFineSeeking ? FINE_SEEKING_DESCRIPTOR : FINE_SEEK_HINT_DESCRIPTOR)}
							</div>
						) : (
							<div className={styles.centre} data-flx="voice.mobile-video-viewer.centre">
								{!isEnded && (
									<button
										type="button"
										className={clsx(styles.glass, styles.centreButton)}
										onClick={(e) => handleJumpButton(e, 'backward')}
										aria-label={i18n._(BACK_TEN_DESCRIPTOR)}
										data-flx="voice.mobile-video-viewer.back-ten"
									>
										<RewindBackTenIcon size={remFromPx(26)} data-flx="voice.mobile-video-viewer.back-ten-icon" />
									</button>
								)}
								<div className={styles.playColumn}>
									<button
										type="button"
										className={clsx(styles.glass, styles.centreButton, styles.playButton)}
										onClick={handlePlayPause}
										aria-label={i18n._(isEnded ? REPLAY_DESCRIPTOR : isPlaying ? PAUSE_DESCRIPTOR : PLAY_DESCRIPTOR)}
										data-flx="voice.mobile-video-viewer.play-pause-button"
									>
										{isEnded ? (
											<ReplayIcon size={remFromPx(30)} data-flx="voice.mobile-video-viewer.replay-icon" />
										) : isPlaying ? (
											<PauseSolidIcon size={remFromPx(30)} data-flx="voice.mobile-video-viewer.pause-icon" />
										) : (
											<PlaySolidIcon size={remFromPx(30)} data-flx="voice.mobile-video-viewer.play-icon" />
										)}
									</button>
									{isEnded && <span className={styles.replayLabel}>{i18n._(REPLAY_DESCRIPTOR)}</span>}
								</div>
								{!isEnded && (
									<button
										type="button"
										className={clsx(styles.glass, styles.centreButton)}
										onClick={(e) => handleJumpButton(e, 'forward')}
										aria-label={i18n._(FORWARD_TEN_DESCRIPTOR)}
										data-flx="voice.mobile-video-viewer.forward-ten"
									>
										<RewindForwardTenIcon size={remFromPx(26)} data-flx="voice.mobile-video-viewer.forward-ten-icon" />
									</button>
								)}
							</div>
						)}
						<div className={styles.bottomArea} data-flx="voice.mobile-video-viewer.bottom-area">
							{isEnded && next && (
								<button
									type="button"
									className={clsx(styles.glass, styles.nextCard)}
									onClick={handleNext}
									data-flx="voice.mobile-video-viewer.next-card"
								>
									{next.isVideo ? (
										<video
											className={styles.nextThumb}
											src={`${next.src}#t=0.1`}
											muted
											playsInline
											preload="metadata"
											tabIndex={-1}
											data-flx="voice.mobile-video-viewer.next-thumb"
										/>
									) : (
										<img
											className={styles.nextThumb}
											src={next.src}
											alt=""
											data-flx="voice.mobile-video-viewer.next-picture"
										/>
									)}
									<span className={styles.nextText}>
										<span className={styles.nextLabel}>{i18n._(NEXT_IN_CHAT_DESCRIPTOR)}</span>
										<span className={styles.nextTitle}>{next.title}</span>
										{next.subtitle && <span className={styles.nextMeta}>{next.subtitle}</span>}
									</span>
									<CaretRightIcon size={remFromPx(18)} data-flx="voice.mobile-video-viewer.next-caret" />
								</button>
							)}
							<div className={clsx(styles.glass, styles.panel)} data-flx="voice.mobile-video-viewer.controls-bar">
								{isScrubbing && (
									<div
										className={styles.preview}
										style={
											{
												'--preview-progress': displayProgress,
												'--preview-width': `${previewWidth}px`,
											} as React.CSSProperties
										}
										aria-hidden="true"
										data-flx="voice.mobile-video-viewer.preview"
									>
										{Accessibility.showVideoSeekPreviewThumbnails && (
											<div className={styles.previewFrame} style={{width: previewWidth, height: previewHeight}}>
												<VideoSeekPreview src={src} time={displayCurrentTime} className={styles.previewVideo} />
											</div>
										)}
										<span className={clsx(styles.glass, styles.previewTime)}>{formatTime(displayCurrentTime)}</span>
									</div>
								)}
								<div className={styles.panelRow}>
									<span className={styles.timeDisplay} data-flx="voice.mobile-video-viewer.time-display">
										{formatTime(displayCurrentTime)}
										<span className={styles.timeTotal}> / {formatTime(duration)}</span>
									</span>
									<span className={styles.panelSpacer} />
									<div className={styles.panelButtons}>
										<button
											type="button"
											className={styles.panelButton}
											onClick={handleToggleMute}
											aria-label={VideoVolume.isMuted ? i18n._(UNMUTE_DESCRIPTOR) : i18n._(MUTE_DESCRIPTOR)}
											data-flx="voice.mobile-video-viewer.mute-button.toggle-mute"
										>
											{VideoVolume.isMuted ? (
												<SpeakerXIcon size={remFromPx(21)} data-flx="voice.mobile-video-viewer.speaker-x-icon" />
											) : (
												<SpeakerHighIcon size={remFromPx(21)} data-flx="voice.mobile-video-viewer.speaker-high-icon" />
											)}
										</button>
										<button
											ref={speedButtonRef}
											type="button"
											className={styles.panelButton}
											onClick={handleOpenSpeed}
											aria-label={i18n._(PLAYBACK_SPEED_DESCRIPTOR)}
											aria-haspopup="menu"
											data-flx="voice.mobile-video-viewer.speed-button"
										>
											<span className={styles.speedChip}>{formatSpeed(rate)}</span>
										</button>
										{forceLoop === undefined && (
											<button
												type="button"
												className={clsx(styles.panelButton, loop && styles.panelButtonOn)}
												onClick={handleToggleLoop}
												aria-label={i18n._(LOOP_DESCRIPTOR)}
												aria-pressed={loop}
												data-flx="voice.mobile-video-viewer.loop-button"
											>
												<RepeatIcon size={remFromPx(21)} data-flx="voice.mobile-video-viewer.loop-icon" />
											</button>
										)}
										{VideoPlayback.canFloat && (
											<button
												type="button"
												className={styles.panelButton}
												onClick={handleFloat}
												aria-label={i18n._(FLOAT_DESCRIPTOR)}
												data-flx="voice.mobile-video-viewer.float-button"
											>
												<PictureInPictureIcon size={remFromPx(21)} data-flx="voice.mobile-video-viewer.float-icon" />
											</button>
										)}
										{canFullscreen && (
											<button
												type="button"
												className={styles.panelButton}
												onClick={handleFullscreen}
												aria-label={i18n._(FULL_SCREEN_DESCRIPTOR)}
												data-flx="voice.mobile-video-viewer.fullscreen-button"
											>
												<DeviceRotateIcon size={remFromPx(21)} data-flx="voice.mobile-video-viewer.rotate-icon" />
											</button>
										)}
									</div>
								</div>
								<div
									className={clsx(styles.progressBarWrapper, isScrubbing && styles.progressBarScrubbing)}
									onPointerDown={handleProgressPointerDown}
									onPointerMove={handleProgressPointerMove}
									onPointerUp={finishScrub}
									onPointerCancel={finishScrub}
									onClick={stop}
									onKeyDown={handleProgressKeyDown}
									role="slider"
									tabIndex={0}
									aria-valuenow={Math.round(displayProgress * 100)}
									aria-valuemin={0}
									aria-valuemax={100}
									aria-valuetext={formatTime(displayCurrentTime)}
									aria-label={i18n._(VIDEO_PROGRESS_DESCRIPTOR)}
									data-flx="voice.mobile-video-viewer.progress-bar-wrapper.progress-seek"
								>
									<div className={styles.progressTrack} data-flx="voice.mobile-video-viewer.progress-track">
										<div
											className={styles.progressBuffered}
											style={{width: `${bufferedProgress * 100}%`}}
											data-flx="voice.mobile-video-viewer.progress-buffered"
										/>
										<div
											className={styles.progressFill}
											style={{width: `${displayProgress * 100}%`}}
											data-flx="voice.mobile-video-viewer.progress-fill"
										/>
										<div
											className={styles.progressThumb}
											style={{left: `${displayProgress * 100}%`}}
											data-flx="voice.mobile-video-viewer.progress-thumb"
										/>
									</div>
								</div>
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
			<GlassMenu
				isOpen={speedMenuOpen}
				onClose={closeSpeedMenu}
				anchor={speedButtonRef.current}
				tone="dark"
				groups={speedGroups}
				title={i18n._(PLAYBACK_SPEED_DESCRIPTOR)}
				data-flx="voice.mobile-video-viewer.speed-menu"
			/>
		</div>
	);
});
